import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { InstagramLogo, WhatsappLogo, CheckCircle, WarningCircle, X, Link as LinkIcon, Trash } from "@phosphor-icons/react";

export default function IntegrationsPage() {
  const [search] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [status, setStatus] = useState(null);
  const [showWA, setShowWA] = useState(false);
  const [wa, setWA] = useState({ display_name: "", business_number: "", phone_number_id: "", access_token: "" });

  const load = useCallback(async () => {
    const [{ data: accs }, { data: st }] = await Promise.all([
      api.get("/integrations/accounts"),
      api.get("/integrations/status"),
    ]);
    setAccounts(accs);
    setStatus(st);
  }, []);
  useEffect(() => {
    load();
    const connected = search.get("connected");
    if (connected) toast.success(`${connected} connected`);
  }, [load, search]);

  const connectInstagram = async () => {
    try {
      const { data } = await api.get("/integrations/instagram/authorize");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Meta App not configured yet");
    }
  };

  const configureWA = async (e) => {
    e.preventDefault();
    try {
      await api.post("/integrations/whatsapp/configure", wa);
      toast.success("WhatsApp connected");
      setShowWA(false);
      setWA({ display_name: "", business_number: "", phone_number_id: "", access_token: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const disconnect = async (id) => {
    if (!window.confirm("Disconnect this account?")) return;
    await api.delete(`/integrations/accounts/${id}`);
    toast.success("Disconnected");
    load();
  };

  const ig = accounts.find((a) => a.provider === "instagram");
  const waAcc = accounts.find((a) => a.provider === "whatsapp");

  return (
    <Layout>
      <div className="border-b border-white/10 px-10 py-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// channels</div>
        <h1 className="font-heading text-5xl tracking-tight mt-2">Integrations</h1>
      </div>

      <div className="p-10 space-y-6">
        {/* WhatsApp card */}
        <div className="border border-white/10 bg-[#121212] p-6">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-sm bg-[#25D366]/10 border border-[#25D366]/30 flex items-center justify-center">
                <WhatsappLogo size={28} weight="fill" className="text-[#25D366]" />
              </div>
              <div>
                <div className="font-heading text-2xl tracking-tight">WhatsApp Business Cloud</div>
                <div className="text-sm text-white/60 mt-1 max-w-xl">
                  Send and receive messages via the official Meta WhatsApp Cloud API. Paste your phone number ID and permanent access token from Meta Business Manager.
                </div>
                {waAcc?.connected && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <CheckCircle size={16} weight="fill" className="text-[#25D366]" />
                    <span>Connected as <span className="font-mono">{waAcc.external_id}</span></span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {waAcc?.connected && (
                <button data-testid="wa-disconnect" onClick={() => disconnect(waAcc.id)}
                  className="border border-white/15 hover:border-[#E1306C]/50 hover:text-[#E1306C] px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors">
                  <Trash size={14} /> Disconnect
                </button>
              )}
              <button data-testid="wa-connect-button" onClick={() => setShowWA(true)}
                className="bg-white text-black px-4 py-2 rounded-sm text-sm font-medium hover:bg-white/90 transition-colors">
                {waAcc?.connected ? "Reconfigure" : "Connect WhatsApp"}
              </button>
            </div>
          </div>
        </div>

        {/* Instagram card */}
        <div className="border border-white/10 bg-[#121212] p-6">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-sm bg-[#E1306C]/10 border border-[#E1306C]/30 flex items-center justify-center">
                <InstagramLogo size={28} weight="fill" className="text-[#E1306C]" />
              </div>
              <div>
                <div className="font-heading text-2xl tracking-tight">Instagram Business</div>
                <div className="text-sm text-white/60 mt-1 max-w-xl">
                  Connect via Meta OAuth to send DMs and receive events (new followers, likes, story replies, mentions, comments) through the Instagram Graph API.
                </div>
                {!status?.meta_configured && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-yellow-400">
                    <WarningCircle size={16} weight="fill" />
                    <span>Meta App not configured — set META_APP_ID, META_APP_SECRET and META_REDIRECT_URI in <span className="font-mono">backend/.env</span></span>
                  </div>
                )}
                {ig?.connected && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <CheckCircle size={16} weight="fill" className="text-[#E1306C]" />
                    <span>Connected as <span className="font-mono">{ig.display_name}</span></span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {ig?.connected && (
                <button data-testid="ig-disconnect" onClick={() => disconnect(ig.id)}
                  className="border border-white/15 hover:border-[#E1306C]/50 hover:text-[#E1306C] px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors">
                  <Trash size={14} /> Disconnect
                </button>
              )}
              <button data-testid="ig-connect-button" onClick={connectInstagram} disabled={!status?.meta_configured}
                className="bg-white text-black px-4 py-2 rounded-sm text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                <LinkIcon size={14} weight="bold" /> Connect with Meta
              </button>
            </div>
          </div>
        </div>

        {/* Webhook info */}
        <div className="border border-white/10 bg-[#121212] p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/40 mb-4">Webhooks</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-white/40 font-mono text-xs">Instagram callback</div>
              <div data-testid="webhook-instagram-url" className="font-mono text-xs mt-1 break-all p-2 border border-white/10 rounded-sm bg-black/40">
                {`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/instagram`}
              </div>
              <div className="text-white/40 text-xs mt-2">
                Verify token: {status?.instagram_webhook_verify_configured ? <span className="text-[#25D366]">configured</span> : <span className="text-yellow-400">missing (set META_WEBHOOK_VERIFY_TOKEN)</span>}
              </div>
            </div>
            <div>
              <div className="text-white/40 font-mono text-xs">WhatsApp callback</div>
              <div data-testid="webhook-whatsapp-url" className="font-mono text-xs mt-1 break-all p-2 border border-white/10 rounded-sm bg-black/40">
                {`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/whatsapp`}
              </div>
              <div className="text-white/40 text-xs mt-2">
                Verify token: {status?.whatsapp_webhook_verify_configured ? <span className="text-[#25D366]">configured</span> : <span className="text-yellow-400">missing (set WHATSAPP_WEBHOOK_VERIFY_TOKEN)</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showWA && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/15 w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// connect</div>
                <div className="font-heading text-2xl tracking-tight">WhatsApp Cloud API</div>
              </div>
              <button onClick={() => setShowWA(false)} className="p-2 hover:bg-white/10 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={configureWA} className="p-6 space-y-3">
              {[
                ["display_name", "Display name", "My Business"],
                ["business_number", "Business phone (E.164)", "+5511999999999"],
                ["phone_number_id", "Phone Number ID (Meta)", "106540352242922"],
                ["access_token", "Permanent access token", "EAA…"],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">{label}</label>
                  <input data-testid={`wa-form-${k}`} value={wa[k]} onChange={(e) => setWA({...wa, [k]: e.target.value})} required placeholder={ph}
                    className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors font-mono text-sm" />
                </div>
              ))}
              <button data-testid="wa-form-submit" type="submit"
                className="w-full bg-white text-black py-2.5 rounded-sm font-medium hover:bg-white/90 transition-colors">
                Save connection
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
